import React from 'react';
import { Mic2, Save, Info, AlertCircle } from 'lucide-react';

const NoiseCancellationTab = ({ agent, formData, setFormData, onSubmit }) => {
    const dtlnEnabled = formData.dtln_enabled ?? false;
    const dtlnStrength = formData.dtln_strength ?? 0.5;
    const aiAcousticsEnabled = formData.ai_acoustics_enabled ?? false;

    const handleToggle = (e) => {
        e.preventDefault();
        const next = !dtlnEnabled;
        setFormData({
            ...formData,
            dtln_enabled: next,
            ...(next ? { ai_acoustics_enabled: false } : {}),
        });
    };

    const handleStrengthChange = (e) => {
        setFormData({ ...formData, dtln_strength: parseFloat(e.target.value) });
    };

    return (
        <div
            className="flex-1 overflow-y-auto min-h-0 p-6"
            style={{ scrollBehavior: 'smooth', maxHeight: 'calc(100vh - 280px)' }}
        >
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <Mic2 className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-foreground">DTLN Noise Cancellation</h3>
                            <p className="text-sm text-muted-foreground">
                                Self-hosted noise suppression — no cloud API, no per-minute fees
                            </p>
                        </div>
                    </div>

                    {/* Info banner */}
                    <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4">
                        <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-300 space-y-1">
                            <p className="font-medium text-blue-200">For self-hosted only</p>
                            <p>
                                DTLN runs entirely in-process via ONNX Runtime (~8 ms latency). When enabled it
                                replaces the BVC noise filter. Do <strong>not</strong> enable on
                                the Cloud instance — it already has built-in noise cancellation and
                                chaining two models produces unexpected results.
                            </p>
            
                        </div>
                    </div>

                    {aiAcousticsEnabled && !dtlnEnabled && (
                        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4">
                            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-300">
                                ai-coustics is enabled on this agent. Enabling DTLN will turn ai-coustics off.
                            </p>
                        </div>
                    )}

                    {/* Enable / Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                        <div>
                            <label className="text-sm font-medium text-foreground">Enable DTLN Noise Cancellation</label>
                            <p className="text-xs text-muted-foreground mt-1">
                                Suppress background noise using the DTLN LSTM model (self-hosted)
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                dtlnEnabled ? 'bg-primary' : 'bg-muted'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    dtlnEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Strength Slider — only visible when enabled */}
                    {dtlnEnabled && (
                        <div className="mt-4 p-4 bg-muted/20 rounded-lg border border-border/50 space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-foreground">Suppression Strength</label>
                                <span className="text-xs text-muted-foreground font-mono">
                                    {dtlnStrength.toFixed(2)}
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={dtlnStrength}
                                onChange={handleStrengthChange}
                                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>0.0 — bypass</span>
                                <span>0.5 — balanced</span>
                                <span>1.0 — full</span>
                            </div>
                            <p className="text-xs text-muted-foreground pt-1">
                                Wet/dry blend. At <strong>0.5</strong> the output is an equal mix of the denoised
                                signal and the original. Lower values preserve more original audio — useful if the
                                model is over-suppressing speech (e.g. on SIP/telephone audio).
                            </p>
                        </div>
                    )}
                </div>

                {/* Disabled state placeholder */}
                {!dtlnEnabled && (
                    <div className="bg-muted/20 rounded-lg border border-border/50 p-8 text-center">
                        <Mic2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-sm text-muted-foreground">
                            Enable DTLN noise cancellation to configure suppression strength
                        </p>
                    </div>
                )}

                {/* Save Button */}
                {agent?.id && (
                    <div className="flex justify-end pt-4 border-t border-border/50">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onSubmit) {
                                    const syntheticEvent = {
                                        preventDefault: () => {},
                                        target: e.target.closest('form') || e.target,
                                        currentTarget: e.target.closest('form') || e.target,
                                    };
                                    onSubmit(syntheticEvent);
                                }
                            }}
                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 font-medium"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoiseCancellationTab;
